import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorText } from '../lib/api';
import { useAuth } from '../lib/auth';
import { clock } from '../lib/format';
import type { EpisodeDetail, EpisodeSummary } from '../types';

type AdminEpisode = EpisodeSummary & { mediaKind: string; mediaKey: string | null; mediaAccess: string; segments: number; translated: number; reviewed: number };
interface Job {
  id: string;
  kind: string;
  status: string;
  error: string | null;
  title: string | null;
  model: string | null;
  input_tokens: number;
  output_tokens: number;
  updated_at: number;
}

export function AdminPage() {
  const { me } = useAuth();
  const { id } = useParams();
  if (!me) return <p className="page muted">加载中…</p>;
  if (me.user?.role !== 'admin') return <p className="page">需要管理员账号。</p>;
  return id ? <EpisodeEditor id={id} /> : <EpisodeList />;
}

function EpisodeList() {
  const [episodes, setEpisodes] = useState<AdminEpisode[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(() => {
    api<{ episodes: AdminEpisode[] }>('/admin/episodes').then((d) => setEpisodes(d.episodes), (e) => setMsg(errorText(e)));
    api<{ jobs: Job[] }>('/admin/jobs').then((d) => setJobs(d.jobs), () => undefined);
  }, []);
  useEffect(load, [load]);
  return (
    <div className="page">
      <div className="row spread">
        <h2>内容审核</h2>
        <button
          onClick={async () => {
            await api('/admin/discover', { method: 'POST', json: {} });
            setMsg('已开始检查 BBC 新一期，稍后刷新');
            setTimeout(load, 8000);
          }}
        >
          检查新一期
        </button>
      </div>
      {msg && <p className="status">{msg}</p>}
      <table className="table">
        <thead>
          <tr>
            <th>日期</th>
            <th>标题</th>
            <th>状态</th>
            <th>中文</th>
            <th>已审</th>
            <th>视频</th>
          </tr>
        </thead>
        <tbody>
          {episodes.map((e) => (
            <tr key={e.id}>
              <td>{e.publishedOn}</td>
              <td>
                <Link to={`/admin/${e.id}`}>{e.title}</Link>
              </td>
              <td>
                {e.status}
                {e.statusDetail ? <small className="muted"> · {e.statusDetail}</small> : null}
              </td>
              <td>
                {e.translated}/{e.segments}
              </td>
              <td>{e.reviewed}</td>
              <td>
                {e.mediaKind}
                {e.mediaKind === 'r2' ? ` (${e.mediaAccess})` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <h3>最近任务</h3>
      <ul className="plain-list">
        {jobs.map((j) => (
          <li key={j.id}>
            <small>
              {new Date(j.updated_at).toLocaleString()} · {j.kind} · {j.status} · {j.title} {j.model ? `· ${j.model} ${j.input_tokens}/${j.output_tokens} tokens` : ''}
              {j.error ? <span className="status"> · {j.error}</span> : null}
            </small>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EpisodeEditor({ id }: { id: string }) {
  const [data, setData] = useState<(EpisodeDetail & { admin: { mediaKind: string; mediaKey: string | null; mediaAccess: string } }) | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [lessonJson, setLessonJson] = useState('');
  const load = useCallback(
    () =>
      api<NonNullable<typeof data>>(`/admin/episodes/${id}`).then((d) => {
        setData(d);
        const items = [
          ...d.lesson.expressions.map((x) => ({ kind: 'expression', text: x.text, meaning: x.meaning, segmentId: x.segmentId, payload: { explanation: x.explanation, examples: x.examples } })),
          ...(d.lesson.gist ? [{ kind: 'gist_quiz', text: d.lesson.gist.text, payload: { options: d.lesson.gist.options, answer: d.lesson.gist.answer, explanation: d.lesson.gist.explanation } }] : []),
          ...(d.lesson.prompt ? [{ kind: 'output_prompt', text: d.lesson.prompt.text, payload: { hint: d.lesson.prompt.hint } }] : []),
        ];
        setLessonJson(JSON.stringify(items, null, 2));
      }, (e) => setMsg(errorText(e))),
    [id],
  );
  useEffect(() => {
    void load();
  }, [load]);
  if (!data) return <p className="page muted">{msg ?? '加载中…'}</p>;
  const ep = data.episode;
  const act = async (label: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
      setMsg(`${label}：完成`);
      await load();
    } catch (e) {
      setMsg(`${label}：${errorText(e)}`);
    }
  };
  const patch = (json: Record<string, unknown>) => act('保存', () => api(`/admin/episodes/${id}`, { method: 'PATCH', json }));

  return (
    <div className="page wide">
      <Link to="/admin">← 返回列表</Link>
      <h2>{ep.title}</h2>
      <p className="muted">
        {ep.publishedOn} · {ep.status} · {ep.translationOrigin}
      </p>
      {msg && <p className="status">{msg}</p>}
      <div className="row wrap">
        {ep.status !== 'published' ? (
          <button className="primary" onClick={() => patch({ status: 'published' })}>
            发布
          </button>
        ) : (
          <button onClick={() => patch({ status: 'draft' })}>撤回为草稿</button>
        )}
        <button onClick={() => act('重新生成（只补缺失部分）', () => api(`/admin/episodes/${id}/enrich`, { method: 'POST' }))}>AI 补全翻译和课程</button>
        <Link className="button" to={`/?ep=${id}`}>
          预览
        </Link>
      </div>

      <section className="card stack">
        <h3>视频来源</h3>
        <MediaForm admin={data.admin} onSave={(v) => patch(v)} />
        <small className="muted">授权确认前，自托管视频（r2）请保持 tester，仅测试账号可播放；公开版本建议使用官方 YouTube 视频 ID。</small>
      </section>

      <section className="card stack">
        <h3>课程内容（JSON）</h3>
        <textarea className="mono" rows={14} value={lessonJson} onChange={(e) => setLessonJson(e.target.value)} />
        <button onClick={() => act('保存课程', async () => api(`/admin/episodes/${id}/lesson`, { method: 'PUT', json: { items: JSON.parse(lessonJson) } }))}>保存课程</button>
      </section>

      <section className="card">
        <h3>逐句校订</h3>
        <ol className="review-lines">
          {data.segments.map((s) => (
            <li key={s.id} className={s.zhReviewed ? 'reviewed' : ''}>
              <small className="muted mono">{clock(s.startMs)}</small>
              <div className="stack tight">
                <span>{s.en}</span>
                <input
                  defaultValue={s.zh}
                  onBlur={(e) => e.target.value !== s.zh && void api(`/admin/segments/${s.id}`, { method: 'PATCH', json: { zh: e.target.value, zhReviewed: true } }).then(() => setMsg(`已保存第 ${s.idx + 1} 句`))}
                />
              </div>
              <label className="row tight">
                <input
                  type="checkbox"
                  defaultChecked={s.zhReviewed}
                  onChange={(e) => void api(`/admin/segments/${s.id}`, { method: 'PATCH', json: { zhReviewed: e.target.checked } })}
                />
                已审
              </label>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function MediaForm({ admin, onSave }: { admin: { mediaKind: string; mediaKey: string | null; mediaAccess: string }; onSave(v: Record<string, unknown>): void }) {
  const [kind, setKind] = useState(admin.mediaKind);
  const [key, setKey] = useState(admin.mediaKey ?? '');
  const [access, setAccess] = useState(admin.mediaAccess === 'public' ? 'public' : 'tester');
  return (
    <div className="row wrap">
      <select value={kind} onChange={(e) => setKind(e.target.value)}>
        <option value="none">暂无</option>
        <option value="youtube">YouTube 视频 ID</option>
        <option value="r2">自托管（R2 key）</option>
        <option value="remote">外部链接</option>
      </select>
      <input value={key} onChange={(e) => setKey(e.target.value)} placeholder="ID / key / URL" />
      {kind === 'r2' && (
        <select value={access} onChange={(e) => setAccess(e.target.value)}>
          <option value="tester">仅测试账号</option>
          <option value="public">公开</option>
        </select>
      )}
      <button onClick={() => onSave({ mediaKind: kind, mediaKey: key || null, mediaAccess: access })}>保存</button>
    </div>
  );
}
